import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { Navigate } from 'react-router'
import api from '../utils/api'
import { SafeHTML } from '../utils/sanitize'

const AdminEmails = () => {
  const { user, isLoading } = useAuth()
  const [emails, setEmails] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 })
  const [selectedEmail, setSelectedEmail] = useState(null)
  const [showEmailModal, setShowEmailModal] = useState(false)

  useEffect(() => {
    const fetchEmails = async () => {
      try {
        const response = await api.get(`/admin/emails?page=${pagination.page}&limit=${pagination.limit}`)
        setEmails(response.data.emails)
        setPagination(prev => ({
          ...prev,
          ...response.data.pagination
        }))
      } catch (error) {
        setError('Fout bij ophalen van email logs')
        console.error('Fetch emails error:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchEmails()
  }, [pagination.page, pagination.limit])

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('nl-NL')
  }

  const getStatusColor = (email) => {
    if (email.error_message) {
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
    }
    if (email.sent_at) {
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
    }
    return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
  }

  const getStatusText = (email) => {
    if (email.error_message) {
      return 'Gefaald'
    }
    if (email.sent_at) {
      return 'Verzonden'
    }
    return 'Onbekend'
  }

  const showEmailDetails = (email) => {
    setSelectedEmail(email)
    setShowEmailModal(true)
  }

  const closeEmailModal = () => {
    setSelectedEmail(null)
    setShowEmailModal(false)
  }

  if (isLoading) {
    return <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
    </div>
  }

  if (!user || !user.isAdmin) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="container mx-auto px-4 py-8 min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Email Logs</h1>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Totaal: {pagination.total} emails
        </div>
      </div>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-sm mb-4">
          {error}
        </div>
      )}

      {/* Statistics Cards */}
      <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">📧</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Totaal Emails
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {pagination.total}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="shrink-0">
                <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">❌</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Gefaald
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {emails.filter(e => e.error_message).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Details Modal */}
      {showEmailModal && selectedEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Email Details</h2>
              <button
                onClick={closeEmailModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Algemene Informatie</h3>
                <div className="space-y-2 text-gray-900 dark:text-gray-300">
                  <div>
                    <span className="font-medium">Onderwerp:</span> {selectedEmail.subject}
                  </div>
                  <div>
                    <span className="font-medium">Type:</span> {selectedEmail.email_type}
                  </div>
                  <div>
                    <span className="font-medium">Ontvanger:</span> {selectedEmail.user_email || selectedEmail.recipient_email}
                  </div>
                  {selectedEmail.user_name && (
                    <div>
                      <span className="font-medium">Gebruiker:</span> {selectedEmail.user_name}
                    </div>
                  )}
                  {selectedEmail.meetup_title && (
                    <div>
                      <span className="font-medium">Meetup:</span> {selectedEmail.meetup_title}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Status:</span>{' '}
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedEmail)}`}>
                      {getStatusText(selectedEmail)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Timestamps</h3>
                <div className="space-y-2 text-gray-900 dark:text-gray-300">
                  <div>
                    <span className="font-medium">Verzonden:</span> {formatDate(selectedEmail.sent_at)}
                  </div>
                  {selectedEmail.error_message && (
                    <div>
                      <span className="font-medium text-red-600 dark:text-red-400">Fout:</span>
                      <div className="mt-1 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-sm text-sm text-red-700 dark:text-red-300">
                        {selectedEmail.error_message}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {selectedEmail.email_content && (
              <div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Email Inhoud</h3>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700 max-h-96 overflow-y-auto">
                  <SafeHTML
                    html={selectedEmail.email_content}
                    className="prose dark:prose-invert max-w-none"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={closeEmailModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Emails Table */}
      {loading ? (
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ontvanger
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Verzonden
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {emails.map((email) => (
                <tr key={email.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {email.subject}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {email.email_type}
                        {email.meetup_title && (
                          <span className="ml-2 text-blue-600 dark:text-blue-400">• {email.meetup_title}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm text-gray-900 dark:text-white">
                        {email.user_email || email.recipient_email}
                      </div>
                      {email.user_name && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">{email.user_name}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(email)}`}>
                      {getStatusText(email)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {formatDate(email.sent_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => showEmailDetails(email)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-500 px-2 py-1 text-xs border border-blue-600 dark:border-blue-500 rounded-sm hover:bg-blue-50 dark:hover:bg-blue-900/30"
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page <= 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Vorige
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Volgende
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Pagina <span className="font-medium">{pagination.page}</span> van{' '}
                    <span className="font-medium">{pagination.pages}</span> -{' '}
                    <span className="font-medium">{pagination.total}</span> emails totaal
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-xs -space-x-px">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page <= 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      Vorige
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page >= pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      Volgende
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default AdminEmails